import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDeleteConfirm } from '../../hooks/useDeleteConfirm';
import { localDayKey } from '../../utils/localDate';
import { useMoney } from '../../hooks/useMoney';
import '../../styles/components/FormPage.css';
import { parseIdFromPath } from '../../utils/jiraId';
import Icon from '../Icon';
import { Input as FormInput } from '../FormField';
import ConfirmDialog from '../ui/ConfirmDialog';
import RichTextEditor from '../ui/RichTextEditor';
import SearchableSelect from '../ui/SearchableSelect';
import LineItemsEditor, { LineItem, LineItemFieldConfig } from '../ui/LineItemsEditor';
import { useLocation } from 'react-router-dom';
import { createContributor, updateContributor, archiveContributor, restoreContributor, deleteContributor, getContributorById } from '../../services/ContributorService';
import { loadSettlements } from '../../services/SettlementService';
import { Contributor, ContributionItem } from '../../types/ui/Settlement';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors } from './_shared';
import { useSettings } from '../../contexts/SettingsContext';
import { loadBudget, refreshBudgetFromApi } from '../../utils/budgetStorage';
import { BudgetCategory } from '../../types/ui/Budget';
import { PHONE_PLACEHOLDER } from '../../constants/placeholders';

const ROLES: { value: string; label: string }[] = [
  { value: 'couple',    label: 'Couple' },
  { value: 'family',    label: 'Family' },
  { value: 'friend',    label: 'Friend' },
  { value: 'organizer', label: 'Organiser' },
  { value: 'vendor',    label: 'Vendor' },
];

const METHODS: { value: NonNullable<Contributor['payment_method']>; label: string }[] = [
  { value: 'bank-transfer', label: 'Bank Transfer' },
  { value: 'cash',          label: 'Cash' },
  { value: 'card',          label: 'Card' },
  { value: 'mobile-money',  label: 'Mobile Money' },
];


interface ContributorFormPageProps {
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}

export const ContributorFormPage: React.FC<ContributorFormPageProps> = ({ onSave, onCancel, onDelete }) => {
  const money = useMoney();
  const { showSuccess, showError, showInfo } = useNotifications();
  const fieldErrors = useFieldErrors();
  const { formatCurrency } = useSettings();
  const location = useLocation();
  const editContributor = (location.state as { contributor?: Contributor } | null)?.contributor ?? null;
  // Detect edit mode from URL even after hard refresh (no route state)
  const urlContributorId = useMemo(
    () => parseIdFromPath('CN', location.pathname, '/payments/contributor/'),
    [location.pathname],
  );
  const isEditMode = editContributor !== null || urlContributorId !== null;
  const editId = editContributor?.id ?? urlContributorId;
  const today = localDayKey();

  // Core fields
  const [name, setName]                   = useState(editContributor?.name ?? '');
  const [email, setEmail]                 = useState(editContributor?.email ?? '');
  const [phone, setPhone]                 = useState(editContributor?.phone ?? '');
  const [role, setRole]                   = useState<Contributor['role']>(editContributor?.role ?? 'family');
  const [paymentMethod, setPaymentMethod] = useState<NonNullable<Contributor['payment_method']>>(editContributor?.payment_method ?? 'bank-transfer');
  const [notes, setNotes]                 = useState(editContributor?.notes ?? '');
  // Carried through unchanged: the team directory (/team) is disabled in the
  // API, so this form no longer offers a team-member picker, but an existing
  // link must not be wiped when the contributor is saved.
  const [teamMemberId, setTeamMemberId]   = useState<string | null>(editContributor?.team_member_id ?? null);

  // Contribution items (what specific things they paid for) — edited
  // directly in place via LineItemsEditor (see contributionEditorItems/
  // handleContributionItemsChange below), the same inline-row pattern
  // procurement/quote/bundle line items already use instead of a
  // click-row-to-load-a-form-below flow with its own parallel state.
  const [items, setItems] = useState<ContributionItem[]>(
    (editContributor?.items_paid_for ?? []).map(i => ({ ...i, reimbursable: i.reimbursable ?? true }))
  );
  const [archived, setArchived] = useState(!!editContributor?.archived);

  // Real budget categories to populate the picker — loaded from cache first
  // (instant) then refreshed from the API, same pattern as BudgetTracker.tsx.
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>(() => loadBudget());
  useEffect(() => {
    refreshBudgetFromApi().then(setBudgetCategories).catch(() => {});
  }, []);
  const categoryOptions = useMemo(
    () => budgetCategories
      .filter(c => !c.archived)
      .map(c => ({ value: c.id, label: c.name })),
    [budgetCategories]
  );

  // Re-fetch fresh data from the API so stale route state is replaced. Also
  // handles hard refresh: if no route state but URL has a numeric contributor
  // ID, load it. Keyed on the contributor's id rather than mount, because
  // moving between two contributors client-side reuses this component and
  // would otherwise leave the previous one's values in the fields.
  useEffect(() => {
    const id = editId;
    if (!id) return;
    getContributorById(id).then(fresh => {
      if (!fresh) return;
      setName(fresh.name ?? '');
      setEmail(fresh.email ?? '');
      setPhone(fresh.phone ?? '');
      setRole(fresh.role ?? 'family');
      setPaymentMethod(fresh.payment_method ?? 'bank-transfer');
      setNotes(fresh.notes ?? '');
      setTeamMemberId(fresh.team_member_id ?? null);
      setItems((fresh.items_paid_for ?? []).map(i => ({ ...i, reimbursable: i.reimbursable ?? true })));
      setArchived(!!fresh.archived);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const [saving, setSaving] = useState(false);


  useEffect(() => {
    window.addEventListener('contributor-form-cancel', onCancel);
    return () => window.removeEventListener('contributor-form-cancel', onCancel);
  }, [onCancel]);

  const [confirmArchive, setConfirmArchive] = useState(false);
  // Real dependent-data count: contributors are NOT leaf records — ans-backend's
  // pl_settlements.from_contributor_id / to_contributor_id both cascade-delete
  // (db/migrations/20260607_settlement_tables.sql), same as contribution_items.
  // items.length already covers contribution items; this covers settlements,
  // which aren't otherwise visible on this page. ans-backend now has an `archived`
  // column on contributors (PUT /contributors/:id accepts {archived}), so this
  // is archive-first like the other entities — permanent delete (which still
  // cascades the above) is only reachable once the contributor is archived.
  const [linkedSettlementCount, setLinkedSettlementCount] = useState(0);

  useEffect(() => {
    if (!editId) return;
    loadSettlements()
      .then(settlements => {
        const count = settlements.filter(s => s.from_contributor_id === editId || s.to_contributor_id === editId).length;
        setLinkedSettlementCount(count);
      })
      .catch(() => {});
  }, [editId]);

  const doArchive = useCallback(async () => {
    if (!editId) return;
    setConfirmArchive(false);
    try {
      await archiveContributor(editId);
      setArchived(true);
      showInfo(`"${name}" archived — not permanently deleted. You can restore it from the header.`, { persistent: true });
      onDelete?.();
    } catch {
      showError('Failed to archive contributor');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, name, onDelete]);

  const doRestore = useCallback(async () => {
    if (!editId) return;
    try {
      await restoreContributor(editId);
      setArchived(false);
      showSuccess(`"${name}" restored`);
    } catch {
      showError('Failed to restore contributor');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, name]);

  // Delete forever is only reachable once the contributor is archived (the
  // header button is shown only then). The dialog stays open with the reason.
  const forever = useDeleteConfirm<string>({
    record: 'contributor',
    perform: async id => {
      const ok = await deleteContributor(id);
      if (!ok) throw new Error("This contributor couldn't be deleted. Try again, or leave it archived.");
    },
    onDone: () => {
      showSuccess(`${name} deleted`, { persistent: true });
      onDelete?.();
      onCancel();
    },
  });
  const doDelete = () => { if (editId) forever.request(editId); };

  useEffect(() => {
    if (!isEditMode || !editId) return;
    const handler = () => setConfirmArchive(true);
    window.addEventListener('contributor-form-delete', handler);
    return () => window.removeEventListener('contributor-form-delete', handler);
  }, [isEditMode, editId, name, showSuccess, showError, onDelete, onCancel]);

  useEffect(() => {
    const handler = () => { if (!saving) void handleSave(); };
    window.addEventListener('contributor-form-save', handler);
    return () => window.removeEventListener('contributor-form-save', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email, phone, role, paymentMethod, notes, items, saving]);

  // ContributionItem[] <-> LineItemsEditor's LineItem[] (flat string-keyed
  // values). category_id round-trips through category_name too, so a row
  // that hasn't been touched keeps showing the category it was saved with
  // even before budgetCategories has loaded.
  const contributionEditorItems: LineItem[] = useMemo(() => items.map(i => ({
    id: i.id,
    values: {
      description: i.description,
      category_id: i.category_id ? String(i.category_id) : '',
      category_name: i.category ?? '',
      amount: String(i.amount || ''),
      date: i.date,
      reimbursable: (i.reimbursable ?? true) ? 'true' : 'false',
      // Read-only — live-derived server-side from payments linked via
      // funding_contribution_item_id, never editable here. Absent (not '0')
      // when nothing's been linked yet, so the column can tell "no payment
      // linked" apart from "linked and fully spent."
      remaining: i.funding_contribution_item_id
        ? formatCurrency(i.remaining ?? Number(i.amount))
        : '',
    },
  })), [items, formatCurrency]);

  const handleContributionItemsChange = (next: LineItem[]) => {
    setItems(next.map(row => {
      return {
        id: row.id,
        description: row.values.description,
        amount: Number(row.values.amount) || 0,
        category_id: row.values.category_id ?? '',
        category: categoryOptions.find(o => o.value === row.values.category_id)?.label ?? row.values.category_name,
        date: row.values.date,
        paid_by_contributor_id: editId ?? 'new',
        reimbursable: row.values.reimbursable === 'true',
      };
    }));
  };

  const CONTRIBUTION_FIELDS: LineItemFieldConfig[] = [
    { key: 'description', type: 'text', header: 'Description', placeholder: 'e.g. Rainbow Marquee deposit' },
    { key: 'category_id', type: 'select', header: 'Category', options: categoryOptions, width: 170 },
    { key: 'amount', type: 'currency', header: `Amount (${money.unit})`, width: 140 },
    { key: 'date', type: 'date', header: 'Date', width: 140 },
    { key: 'reimbursable', type: 'checkbox', header: 'Reimburse?', label: 'Reimburse', width: 90 },
    // Blank for an item with no payment linked yet — there's no "Funded by
    // contribution" picker to set that link yet (only settable via direct
    // SQL today), so most rows won't show this until that ships.
    { key: 'remaining', type: 'readonly', header: 'Remaining', width: 120 },
  ];

  const totalContributed  = items.reduce((s, i) => s + Number(i.amount), 0);
  const totalReimbursable = items.filter(i => i.reimbursable).reduce((s, i) => s + Number(i.amount), 0);
  const totalGifted       = items.filter(i => !i.reimbursable).reduce((s, i) => s + Number(i.amount), 0);

  // Auto-computed: pending if anything needs reimbursing, settled otherwise
  const computedStatus: Contributor['reimbursement_status'] =
    items.length === 0 || totalReimbursable === 0 ? 'settled' : 'pending';

  const handleSave = useCallback(async () => {
    if (!name.trim()) { showError('Name is required'); return; }
    fieldErrors.clear();
    setSaving(true);
    try {
      // A row LineItemsEditor lets sit half-filled (blank description, no
      // category picked yet) never used to leave the old add-form's fields
      // at all — the picker's dropdown-required validation blocked it. Same
      // gate here at the save boundary instead, since category_id is a
      // required FK ans-backend's ledger-posting logic needs (see
      // ContributionItem's own doc comment).
      const completeItems = items.filter(i => i.description.trim() && i.amount > 0 && !!i.category_id);
      const reimbursable = completeItems.filter(i => i.reimbursable).reduce((s, i) => s + Number(i.amount), 0);
      const autoStatus: Contributor['reimbursement_status'] =
        completeItems.length === 0 || reimbursable === 0 ? 'settled' : 'pending';
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        role,
        payment_method: paymentMethod,
        reimbursement_status: autoStatus,
        notes: notes.trim() || null,
        total_contributed: completeItems.reduce((s, i) => s + Number(i.amount), 0),
        items_paid_for: completeItems,
        net_balance: 0,
        team_member_id: teamMemberId,
      } as any;
      if (isEditMode && editId) {
        await updateContributor(editId, payload);
        showSuccess('Contributor updated', { persistent: true });
      } else {
        await createContributor(payload);
        showSuccess('Contributor added', { persistent: true });
      }
      onSave();
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to save contributor');
    } finally {
      setSaving(false);
    }
  }, [name, email, phone, role, paymentMethod, notes, items, isEditMode, editContributor, showSuccess, showError, onSave]);

  return (
    <div className="detail-layout">
      {/* ── Main form ── */}
      <div className="inline-form-page">
        {archived && (
          <div className="ds-badge ds-badge-neutral cf-archived-banner">
            <span>Archived — restore it below to edit further, or delete it forever</span>
            <button type="button" className="header-action-btn" onClick={doRestore}>Restore</button>
            <button type="button" className="header-action-btn header-btn-danger" onClick={doDelete}>
              <Icon name="trash" size={14} /> Delete forever
            </button>
          </div>
        )}
        <div className="form-col-2">
          {/* Name with inline team autocomplete */}
          <div className="form-group cf-name-field">
            <label className="form-label">Name <span className="form-required">*</span></label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Mummy B"
              value={name}
              onChange={e => { setName(e.target.value); fieldErrors.clear('name'); }}
              autoComplete="off"
            />
            {fieldErrors.errorFor('name') && <span className="form-error-text">{fieldErrors.errorFor('name')}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <SearchableSelect
              value={role ?? 'family'}
              onChange={value => setRole(value as Contributor['role'])}
              options={ROLES}
            />
          </div>
        </div>

        <div className="form-col-2">
          <FormInput
            label="Email"
            type="email"
            placeholder="email@example.com"
            value={email}
            error={fieldErrors.errorFor('email')}
            onChange={e => { setEmail(e.target.value); fieldErrors.clear('email'); }}
          />
          <FormInput
            label="Phone"
            type="tel"
            placeholder={PHONE_PLACEHOLDER}
            value={phone}
            error={fieldErrors.errorFor('phone')}
            onChange={e => { setPhone(e.target.value); fieldErrors.clear('phone'); }}
          />
        </div>

        <div className="form-col-2">
          <div className="form-group">
            <label className="form-label">Preferred Payment Method</label>
            <SearchableSelect
              value={paymentMethod}
              onChange={value => setPaymentMethod(value as NonNullable<Contributor['payment_method']>)}
              options={METHODS}
            />
          </div>
        </div>

        <RichTextEditor
          label="Notes"
          placeholder="Any context about this contributor…"
          rows={2}
          value={notes}
          onChange={val => setNotes(val)}
        />

        {/* Contribution items — same inline-editable-grid pattern
            procurement/quote/bundle line items use (LineItemsEditor), rather
            than a separate table-plus-add-form pair with its own state. */}
        <div className="cf-contributions form-section">
          <LineItemsEditor
            items={contributionEditorItems}
            fields={CONTRIBUTION_FIELDS}
            onChange={handleContributionItemsChange}
            addLabel="Add contribution"
            emptyDefaults={{ date: today, reimbursable: 'true' }}
            section={{ title: 'Contributions' }}
          />
        </div>
      </div>

      {/* ── Sidebar ── */}
      <aside className="detail-sidebar">
        <section className="side-section">
          <h4>Summary</h4>
          <div className="stat-row">
            <span>Contributions</span>
            <strong>{items.length}</strong>
          </div>
          <div className="stat-row">
            <span>Total paid</span>
            <strong>{formatCurrency(totalContributed)}</strong>
          </div>
          {totalReimbursable > 0 && (
            <div className="stat-row">
              <span>Owed back</span>
              <strong className="cf-owed-back-value">{formatCurrency(totalReimbursable)}</strong>
            </div>
          )}
          {totalGifted > 0 && (
            <div className="stat-row">
              <span>Gifted</span>
              <strong className="cf-gifted-value">{formatCurrency(totalGifted)}</strong>
            </div>
          )}
          <div className="stat-row">
            <span>Status</span>
            <strong style={{ /* ds-dynamic: status color from computedStatus */ color: computedStatus === 'settled' ? 'var(--success)' : 'var(--text-dark)' }}>
              {computedStatus.charAt(0).toUpperCase() + computedStatus.slice(1)}
            </strong>
          </div>
        </section>

        <section className="side-section">
          <h4>About Contributors</h4>
          <p className="form-hint form-hint--flush">
            A contributor is anyone who gives money toward the project — family, friends, or sponsors. Record what they paid for using the Contributions table, and mark them as Settled once they've been reimbursed or their contribution is accounted for.
          </p>
        </section>

        {items.length > 0 && (
          <section className="side-section">
            <h4>Breakdown</h4>
            {items.map(item => (
              <div key={item.id} className="stat-row">
                <span className="cf-breakdown-desc">{item.description}</span>
                <div className="cf-breakdown-badge-wrap">
                  <span className={`ds-badge ${item.reimbursable ? 'ds-badge-warning' : 'ds-badge-success'} cf-breakdown-badge`}>
                    <Icon name={item.reimbursable ? 'exchange' : 'gift'} size={11} />
                  </span>
                  <strong>{formatCurrency(Number(item.amount))}</strong>
                </div>
              </div>
            ))}
          </section>
        )}
      </aside>
      <ConfirmDialog
        open={confirmArchive}
        onClose={() => setConfirmArchive(false)}
        onConfirm={doArchive}
        title="Archive this contributor?"
        description="It will be hidden from the active list but not permanently deleted. You can restore it later from the header."
        confirmLabel="Archive"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this contributor forever?"
        subject={name}
        description="This cannot be undone."
        consequences={[
          items.length > 0 ? `${items.length} contribution item${items.length === 1 ? '' : 's'}` : null,
          linkedSettlementCount > 0 ? `${linkedSettlementCount} settlement${linkedSettlementCount === 1 ? '' : 's'}` : null,
          'Their contact details and notes',
        ].filter((line): line is string => line !== null)}
        confirmLabel="Delete forever"
      />
    </div>
  );
};
